import Link from 'next/link'

import { DatedHeading } from '@/journey/_components/dated-heading'
import { SignoffLine } from '@/journey/_components/signoff-line'
import type { EntryPart, Stand } from '@/journey/_lib/compose-stand'

// Center panel — "where you stand." Daybook prose at editorial scale, replacing
// F's stacked block (chapter cartouche → giant kanji → frame marginalia → CTA stack)
// with one paragraph + one subordinate CTA line. Dominance comes from the 2.2fr
// column at lg and from typographic weight (text-lg → text-2xl) on mobile.
//
// The anchor kanji is set inline in the prose at ~1.85em mincho — punctuation
// rather than icon. F's standalone glyph block, the architectural seam inside
// the panel, and the centered figure structure are all dropped: the paragraph
// is the position, the right-aligned signoff is the action.
//
// Vermilion budget (2/2): ¶ marginal mark in DatedHeading + → arrow in SignoffLine.
// Motion budget (1/5): step-advance translate on CTA hover (in SignoffLine).
export function PanelStand({
    stand,
    contentsHref = '/journey/contents',
}: {
    stand: Stand
    contentsHref?: string
}) {
    return (
        <section
            aria-label="Where you stand"
            className="border-b border-rule-soft py-12 lg:border-r lg:border-b-0 lg:px-10 lg:py-0 xl:px-14"
        >
            <DatedHeading dateLine={stand.dateLine} />
            <EntryProse paragraph={stand.paragraph} />
            <SignoffLine primary={stand.primary} />
            <p className="mt-6 text-meta text-muted-fg italic">
                <Link
                    href={contentsHref}
                    className="group/contents inline-flex items-baseline gap-1.5 rounded-sm outline-focus-ring transition-colors hover:text-body-fg focus-visible:outline-2 focus-visible:outline-offset-4 motion-reduce:transition-none"
                >
                    <span>{`read the way's contents`}</span>
                    <span aria-hidden className="text-muted-fg/70">
                        →
                    </span>
                </Link>
            </p>
        </section>
    )
}

// The body — a single paragraph of running serif. The kanji glyph is set inline
// as a slightly oversized mincho character so it reads as a punctuation mark
// within the sentence rather than a standalone object. Line length stays inside
// reading-comfort bounds (max-w cap below) regardless of the column's width.
function EntryProse({ paragraph }: { paragraph: EntryPart[] }) {
    return (
        <p className="max-w-[38ch] font-serif text-lg leading-prose text-pretty text-ink sm:text-xl sm:leading-[1.65] lg:max-w-[52ch] lg:text-2xl lg:leading-[1.6]">
            {paragraph.map((part, i) => {
                if (part.kind === 'glyph') {
                    return (
                        <span
                            key={i}
                            aria-label={part.meaning}
                            className="mx-[0.05em] inline-block align-baseline font-mincho text-[1.85em] leading-none text-ink select-none"
                        >
                            {part.character}
                        </span>
                    )
                }
                return <span key={i}>{part.text}</span>
            })}
        </p>
    )
}
